import { useEffect, useRef, useState } from 'react'
import { APP_BUILT_AT, APP_NAME, APP_VERSION } from '@/config'
import { CHANGELOG } from '@/data/changelog'
import { compareVersions, whatsNew, type Announcement, type Entry } from '@/lib/whats-new'
import { fmtDate } from '@/logic/dates'
import { useHousehold } from '@/store/household'
import { Button } from './Button'
import { Sheet } from './Sheet'
import './WhatsNew.css'

/** One block per release: the version and its date, then what changed. */
export function ChangelogList({ entries, current }: { entries: Entry[]; current?: string }) {
  // the newest release listed that this device has: the one running may be hidden, so not listed itself
  const have = current && entries.find((e) => compareVersions(e.version, current) <= 0)?.version
  return (
    <div className="whatsnew">
      {entries.map((e) => (
        <section key={e.version} className="whatsnew__release">
          <h3 className="whatsnew__head">
            <span className="whatsnew__version">{e.version}</span>
            <span className="whatsnew__date">{fmtDate(e.date, true)}</span>
            {e.version === have && <span className="whatsnew__current">You're on this</span>}
          </h3>
          <ul className="whatsnew__items">
            {e.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}

/**
 * Once per update, what changed since this device last showed it. An account made before this build
 * that has nothing on record (it was in use before What's new existed) hears about this release; a new
 * account hears nothing until the next one. The build time, not the release date, so an account made on
 * release day is told the right thing whatever the time zone.
 */
export function WhatsNewAnnouncer() {
  const createdAt = useHousehold((s) => s.userDoc?.createdAt)
  const [news, setNews] = useState<Announcement | null>(null)
  const asked = useRef(false)

  useEffect(() => {
    if (asked.current || !createdAt) return
    asked.current = true
    const announceWithoutHistory = createdAt.toMillis() < APP_BUILT_AT
    void whatsNew({ changelog: CHANGELOG, current: APP_VERSION, key: 'cuplzap.whatsNew.lastSeen', announceWithoutHistory }).then(setNews)
  }, [createdAt])

  if (!news) return null
  const dismiss = () => {
    void news.markSeen()
    setNews(null)
  }
  return (
    <Sheet open heading={`What's new in ${APP_NAME}`} variant="dialog" onClose={dismiss} headerEnd={<span />}>
      <p className="whatsnew__lead">{news.from ? `Updated from ${news.from} to ${news.to}.` : `You're on ${news.to}.`}</p>
      <ChangelogList entries={news.entries} />
      {news.more > 0 && (
        <p className="whatsnew__more">
          …and {news.more} earlier update{news.more > 1 ? 's' : ''}, in More → What's new.
        </p>
      )}
      <Button variant="primary" size="lg" block onClick={dismiss}>
        Got it
      </Button>
    </Sheet>
  )
}
